import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, TextField, Typography } from '@mui/material';
import { TablePagination } from '@mui/material';
import { addDays, isValid, startOfDay, subMonths } from 'date-fns';

import AbEnvironment from '../auth/AbEnvironment';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';

// import PayoutsFetch from './PayoutsFetch';
import PayoutsList from './PayoutsList';
import { DatePicker } from '@mui/x-date-pickers';

const abEnvironment = new AbEnvironment();
const PayoutsQuery = graphql`
query PayoutsQuery($accountId: UUID, $startDate: Datetime, $endDate: Datetime, $first: Int, $offset: Int) {
  getresellerpayouttransactions(accountid: $accountId, startts: $startDate, endts: $endDate, first: $first, offset: $offset) {
    totalCount
    ...PayoutsList_pl
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  flex: {
    flex: 1,
  },
  paginationTableFooter: {
    //custom b/c the marginBottom wasn't wanted...
    marginTop: 10
  }
});

class Payouts extends Component {

  constructor(props) {
    super(props);

    // Default toDate
    let cToDate = new Date();
    let tDate = localStorage.getItem('cToDate');
    if (tDate != null) {
      cToDate = new Date(tDate);
    }
    //search todate should be "start of next day"
    let searchToDateUtc = startOfDay(addDays(cToDate, 1)).toISOString();

    let cFromDate = new Date();
    let fDate = localStorage.getItem('cFromDate');
    if (fDate != null) {
      cFromDate = new Date(fDate);
    } else {
      cFromDate = subMonths(cToDate, 1);
    }
    //search fromdate should be "start of day"
    let searchFromDateUtc = startOfDay(cFromDate).toISOString();

    this.state = {
      startDate: cFromDate,
      queryStartDateUtc: searchFromDateUtc,
      endDate: cToDate,
      queryEndDateUtc: searchToDateUtc,
      accountId: this.props.accountId,
      page: 0,
      rowsPerPage: 10,
    };
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  setFromDate(theDate) {
    let vdate = new Date(theDate); // Start of date local timezone.
    if (isValid(vdate)) {
      localStorage.setItem('cFromDate', vdate);
      let searchDateUtc = startOfDay(vdate).toISOString();
      this.setState({ startDate: vdate, queryStartDateUtc: searchDateUtc, page : 0 });
    }
  }

  setToDate(theDate) {
    let vdate = new Date(theDate);
    if (isValid(vdate)) {
      localStorage.setItem('cToDate', vdate);
      let searchDateUtc = startOfDay(addDays(vdate, 1)).toISOString();
      this.setState({ endDate: vdate, queryEndDateUtc: searchDateUtc, page : 0 });
    }
  }

  handleChangePage = (event, page) => {
    this.setState({ page: page });
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page: 0 });
  }

  render() {
    const { isAuthenticated, isUnauthenticatedCustomer, isAuthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    //unauth or needs assent.
    if ((!isAuthenticated() && !isUnauthenticatedCustomer()) || needAssent()) {
      return (<Redirect to='/home' />);
    }

    //don't return things if customer role.
    if (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) {
      return (<></>);
    }

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0) {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;
    let totalCount = 0;

    return (
      <div className={classes.root}>
        <Grid container spacing={2} alignItems='center' style={AppBindStyles.searchContainerOnPaper}>
          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker 
              disableFuture
              label="From date"
              value={this.state.startDate}
              onChange={(newValue) => {
                this.setFromDate(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>

          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker
              disableFuture
              label="To date"
              value={this.state.endDate}
              onChange={(newValue) => {
                this.setToDate(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>
          {/* -- determine if the "download" for payouts is needed
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={() => 
                this.downloadCsv(thePage, theRole, 
                                this.state.queryStartDateUtc, this.state.queryEndDateUtc, theAppSearch,
                                this.state.accountId, this.state.pAccountId, 
                                paymentOrder, 10000, 0)}
            >
              Download payouts&nbsp;&nbsp;<GetApp/>
            </Button>
          </Grid> */}
        </Grid>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={PayoutsQuery}
          variables={{
            startDate: this.state.queryStartDateUtc,
            endDate: this.state.queryEndDateUtc,
            accountId: this.state.accountId,
            first: first,
            offset: offset
          }}
          render={({ error, props }) => {
            if (error) {
              logError(error.message);
              return (
                <ServerError />
              );
            } else if (props) {

              if (props.getresellerpayouttransactions == null) {
                logError("Not returning 'getResellerPayoutTransactions'.");
                return (
                  <div>
                    <Typography variant="body1">View of payouts history not available.</Typography>
                  </div>
                );
              }

              totalCount = props.getresellerpayouttransactions.totalCount;

              return (
                <div>
                  <PayoutsList pl={props.getresellerpayouttransactions}
                    history={this.props.history}
                  />
                  <div className={classes.paginationTableFooter}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />
                  </div>
                </div>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </div>
    );
  }
}

Payouts.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(Payouts);
